import {
    CopyOnHoverDirective,
    CustomCodeUtilService,
    LanguagesService,
    NavigationService,
    ProjectService,
    ReassignmentService,
    SharedInboxService,
    TranslateConfigPipe,
    UIManagerService,
} from '@alpha-core/platform-ui';
import {
    Component,
    DestroyRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    inject,
} from '@angular/core';
import * as _ from 'lodash-es';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Table } from 'primeng/table';
import { environment } from '../../../../environments/environment';
import { CaseService } from '../../../services/case.service';
import { InboxService } from '../../../services/inbox.service';
import { AlphaSharedModule, PrimeNGModules } from '../../../shared/alpha-shared.module';
import { RecoveryModalComponent } from '../recovery-modal/recovery-modal.component';
import { SendSignalModalComponent } from '../send-signal-modal/send-signal-modal.component';
import { VariablesModalComponent } from '../variables-modal/variables-modal.component';

enum RecoveryStatus {
    Ready = 'Ready',
    Processing = 'Processing',
    Complete = 'Complete',
    Failed = 'Failed',
}
@Component({
    selector: 'app-inbox-table',
    standalone: true,
    imports: [AlphaSharedModule, ...PrimeNGModules, CopyOnHoverDirective, TranslateConfigPipe],
    providers: [DialogService],
    templateUrl: './inbox-table.component.html',
    styleUrl: './inbox-table.component.scss',
})
export class InboxTableComponent implements OnInit, OnChanges {
    private projectService = inject(ProjectService);
    dialogService = inject(DialogService);
    private message = inject(MessageService);
    private languageService = inject(LanguagesService);
    private inboxService = inject(InboxService);
    private zone = inject(NgZone);
    private caseService = inject(CaseService);

    sortStatus: 'asc' | 'desc' = 'asc'; // Initialize sort status to ascending by default
    sort: { [key: string]: boolean } = { code: false, name: false }; // Initialize sort object with boolean flags for each field
    destroyRef = inject(DestroyRef);
    mapReAttemptStatus = false;
    selectedCases: Record<string, any>[] = [];
    selectedCaseCopy: Record<string, any>[] = [];
    showReassignDialog = false;

    @Input() currentTask: any;
    @Input() taskType!: 'tasks' | 'signals' | 'recovery' | '';
    @Input() filterOptions: any;
    project: any;
    @Input() inboxList: any;
    @Output() paginationChange = new EventEmitter();
    ref: DynamicDialogRef | undefined;
    @ViewChild('tableRef') tableRef!: Table;

    //sse variables
    eventSource!: EventSource;
    clientId!: string;
    protected messages: any[] = [];
    isFirstTime = false;
    allColumnsHideable = false;

    @Input() selectedTask: any;
    @Input() caseData: any[] = [];
    caseDataTableMappings: Map<any, string>[] = [];

    uiManagerService = inject(UIManagerService);
    navigationService = inject(NavigationService);
    private reassignmentService = inject(ReassignmentService);
    inboxTableClass = '';
    private customCodeUtilService = inject(CustomCodeUtilService);
    _inboxService = inject(SharedInboxService);

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        this.selectedCases = [];
        if (changes['caseData'] && changes['caseData']?.currentValue?.length) {
            this.constructTableMappings();
            // execute afterViewInit trigger
            setTimeout(async () => {
                this.customCodeUtilService.checkReadyAndExecuteHooks(this.executeAfterViewInitHooks);
            });
        }
        if (changes['selectedTask'] && changes['selectedTask'].currentValue) {
            this.inboxTableClass = this.computeInboxTableClass();
        }
        if (changes['currentTask'] && changes['currentTask'].currentValue) {
            if (this.currentTask && this.currentTask?.columns) {
                this.allColumnsHideable = this.currentTask?.columns.every(
                    (column: { hideable: boolean }) => column.hideable,
                );
            }
        }
        if (changes['currentTask']) {
            this.constructTableMappings();
        }
    }

    ngOnInit() {
        this.project = this.projectService.fetchCurrentProject();
    }

    // Function to handle custom sorting
    customSort(field: any) {
        // Check if the field is already being sorted
        if (this.sort[field.name]) {
            // Toggle sort status between ascending and descending
            this.sortStatus = this.sortStatus === 'asc' ? 'desc' : 'asc';
        } else {
            // If the field is not being sorted, set sort status to ascending
            this.sortStatus = 'asc';

            // Set the current field to be sorted and reset other fields
            this.sort[field.name] = true;
            Object.keys(this.sort).forEach((sortKey) => {
                if (sortKey !== field.name) {
                    // Reset other fields to false
                    this.sort[sortKey] = false;
                }
            });
        }
        const mappingPath = this.inboxService.cleanMappingPathString(field.mapping);
        const mappingType = field.mappingType;
        let value = null;
        //Used lodash orderby operator for sorting the caseData;
        switch (mappingType) {
            case 'co':
                value = _.orderBy(this.caseData, [`caseInstance.caseData.data.${mappingPath}`], [this.sortStatus]);
                break;
            case 'case_instance':
                value = _.orderBy(this.caseData, [`caseInstance.${mappingPath}`], [this.sortStatus]);
                break;
            case 'task_instance':
                value = _.orderBy(this.caseData, [`taskInstance.${mappingPath}`], [this.sortStatus]);
                break;
            case 'signal_instance':
                value = _.orderBy(this.caseData, [`signalInstance.${mappingPath}`], [this.sortStatus]);
                break;
            default:
                break;
        }
        if (value) {
            this.caseData = value;
            this.constructTableMappings();
        }
    }
    emitPaginationDetails() {
        this.paginationChange.emit({
            pageNumber: this.filterOptions.pageNumber,
            pageSize: this.filterOptions.pageSize,
            taskName: this.taskType,
        });
    }

    openReassignModal() {
        this.showReassignDialog = true;
    }

    showReassignModal() {
        const { reassignConfig } = this.currentTask || {};
        this.reassignmentService.casesToReassign = this.selectedCases as any;
        this.reassignmentService.inboxType = this.taskType as any;
        this.reassignmentService.inboxColumns = this.currentTask.columns;
        this.reassignmentService.reassignConfig = reassignConfig;
        this.reassignmentService.inboxHooks = this.inboxList.hooks;
        this.reassignmentService.currentTableClass = this.inboxTableClass;
        this.uiManagerService.reset();
        this.navigationService.navigateToReassignment();
    }

    showSendSignalModal() {
        this.ref = this.dialogService.open(SendSignalModalComponent, {
            modal: true,
            width: '70vw',
            header: this.languageService.getTranslation('default-cm-sendSignal') || 'Send Signal',
            data: {
                caseInstances: this.selectedCases,
            },
        });

        this.ref.onClose.subscribe((data) => {
            if (data && Array.isArray(data)) {
                data.map((res: any) => {
                    if (res.success) {
                        this.message.add({ severity: 'success', summary: 'Success!', detail: res.message });
                    } else {
                        this.message.add({
                            severity: 'error',
                            summary: 'Failed!',
                            detail: res.message || `Send signal failed for ${res?.cid}`,
                        });
                    }
                });
            }
            this.emitPaginationDetails();
        });
    }

    showRecoveryDialog() {
        const clientId = window.crypto.randomUUID();
        this.ref = this.dialogService.open(RecoveryModalComponent, {
            modal: true,
            header: this.languageService.getTranslation('default-cm-re-attempt') || 'Re-attempt',
            data: {
                caseInstances: this.selectedCases,
                clientId,
            },
        });

        this.ref.onClose.subscribe((data) => {
            if (data) {
                this.updateCaseDataWithSelectedCases();
                this.selectedCaseCopy = this.selectedCases;
                this.selectedCases = [];
                this.listenForStatusUpdates(clientId);
            }
        });
    }

    listenForStatusUpdates(clientId: string) {
        this.clientId = clientId;
        this.eventSource = new EventSource(environment.ALPHA_UI_ADMIN_SERVICE_URL + `/webhook/sse/${clientId}`);
        this.eventSource.onmessage = ({ data }) => {
            const jsonData: { [key: string]: any } = JSON.parse(data);
            console.log(jsonData);
            if (jsonData?.['clientId'] === this.clientId) {
                this.zone.run(() => {
                    this.messages.push(jsonData);
                    const statusObject = jsonData?.['response'];
                    if (jsonData?.['status'] === 'complete') {
                        this.message.add({
                            severity: 'success',
                            summary: 'Success',
                            detail: jsonData?.['response']?.message || 'Decision made successfully',
                        });
                        const statusArray = [
                            {
                                cid: statusObject?.cid,
                                taskId: statusObject.taskId,
                                status: RecoveryStatus.Complete,
                            },
                        ];
                        this.updateOneCaseData(statusArray);
                    } else {
                        this.message.add({
                            severity: 'error',
                            summary: 'Error!',
                            detail: jsonData?.['response']?.message || 'Decision making was unsuccessful',
                        });
                        const statusArray = [
                            {
                                cid: statusObject?.cid,
                                taskId: statusObject.taskId,
                                status: RecoveryStatus.Failed,
                            },
                        ];
                        this.updateOneCaseData(statusArray);
                    }
                    if (this.messages.length === this.selectedCaseCopy.length) {
                        this.eventSource?.close();
                        this.isFirstTime = false;
                        this.messages = [];
                        this.selectedCaseCopy = [];
                        this.emitPaginationDetails();
                    }
                });
            }
        };
    }

    fetchAndUpdateCaseData() {
        const matchedColumn = this.currentTask?.columns.find((el: any) => el.name == 'reattemptStatus');
        if (!matchedColumn) {
            this.currentTask?.columns?.push({
                sort: false,
                name: 'reattemptStatus',
                label: '{{lang.default-cm-re-attempt-status}}',
                labelType: null,
                mapping: 'recoveryStatus',
                mappingType: 'case_instance',
                type: '',
            });
            this.currentTask?.columns?.push({
                sort: false,
                name: 'processVariables',
                label: '{{lang.default-cm-process-variables}}',
                labelType: null,
                type: 'icon',
            });
            this.currentTask?.columns?.push({
                sort: false,
                name: 'taskVariables',
                label: '{{lang.default-cm-task-variables}}',
                labelType: null,
                type: 'icon',
            });
        }
    }

    updateOneCaseData(statusData: any[]) {
        const matchedIndex = this.caseData.findIndex(
            (caseItem: any) => statusData[0].cid === caseItem.caseInstance.cid,
        );
        this.caseData[matchedIndex].taskInstance.recoveryStatus = statusData[0].status;
    }

    updateCaseDataWithSelectedCases() {
        this.caseData = this.caseData.map((caseItem: any) => {
            const matchedCase = this.selectedCases.find(
                (cases: any) => cases.caseInstance.cid === caseItem.caseInstance.cid,
            );
            if (matchedCase) {
                return {
                    ...caseItem,
                    taskInstance: {
                        ...caseItem.taskInstance,
                        recoveryStatus: RecoveryStatus.Processing,
                    },
                };
            }
            return caseItem;
        });
    }

    async openVariableDialog(caseData: any, variableName: string) {
        if (variableName == 'taskVariables') {
            const variables = this.updateTasks(this.inboxList.recovery.tasks, caseData);
            this.openDialog(variables[0]?.variables, 'default-cm-task-variables');
        } else {
            (await this.caseService.fetchVariables(caseData.taskInstance.currentProcessInstanceId)).subscribe({
                next: (data) => {
                    this.openDialog(data, 'default-cm-process-variables');
                },
                error: (err) => {
                    console.error(err);
                },
            });
        }
    }

    openDialog(data: any, variableName: string) {
        if (data?.length) {
            this.dialogService.open(VariablesModalComponent, {
                modal: true,
                width: '50vw',
                header:
                    this.languageService.getTranslation('default-cm-variable') ||
                    this.languageService.getTranslation(variableName) ||
                    variableName,
                data: {
                    variables: data,
                },
            });
        } else {
            this.message.add({
                severity: 'warn',
                summary: 'Failed!',
                detail: `No variables configured`,
            });
        }
    }

    updateTasks(tasks: any[], caseInstances: Record<string, any>) {
        return tasks.map((task) => {
            const taskId = caseInstances['taskInstance'].taskId;
            const taskNameMatches = task.taskName === caseInstances['taskInstance'].taskName;

            if (!taskId || !taskNameMatches) {
                return []; // Return empty array if taskId or taskName doesn't match
            }

            // Array to hold transformed variables
            const variables = task.variables.map(
                (variable: { bindingValue: { mappingValue: string; mappingType: string }; key: string | number }) => {
                    const modelPath = this.cleanMappingPathString(variable.bindingValue.mappingValue);
                    let value = this.getModelPathValue(variable.bindingValue.mappingType, modelPath, caseInstances);
                    return {
                        name: variable.key,
                        value,
                    };
                },
            );

            return { ...task, variables };
        });
    }

    getModelPathValue(mappingType: string, value: string, caseObject: any) {
        switch (mappingType) {
            case 'case_instance':
                return _.get(caseObject, `caseInstance.${value}`);
            case 'task_instance':
                return _.get(caseObject, `taskInstance.${value}`);
            case 'co':
                return _.get(caseObject, `caseInstance.caseData.data.${value}`);
            case 'number':
                return Number(value);
            case 'boolean':
                return value === 'true';
            default:
                return value;
        }
    }

    cleanMappingPathString(path: string) {
        if (typeof path !== 'string') {
            throw new TypeError('Expected path to be a string');
        }

        if (path.startsWith('$.')) {
            path = path.slice(2); // Removes `$.`
        }

        // Split the string by `.`
        let segments = path.split('.');

        // Remove double quotes from each segment
        segments = segments.map((segment) => {
            // Remove leading double quote if present
            if (segment.startsWith('"')) {
                segment = segment.slice(1);
            }
            // Remove trailing double quote if present
            if (segment.endsWith('"')) {
                segment = segment.slice(0, -1);
            }
            return segment;
        });

        // Join the segments back together
        path = segments.join('.');

        return path;
    }

    computeInboxTableClass(): string {
        const taskKey = this.selectedTask?.key || '';
        const taskName = this.selectedTask?.name?.replace(/ /g, '-') || '';
        return `admin-inbox-${taskKey} admin-inbox-${taskName}`;
    }

    executeAfterViewInitHooks = async () => {
        if (this.inboxList?.hooks?.afterViewInit) {
            await this.inboxService.executeAction(this.inboxList.hooks?.afterViewInit?.triggers, {
                data: this.caseData,
                element: this.tableRef.el.nativeElement,
                operation: 'inbox',
                taskType: this.taskType,
            });
        }
        this.constructTableMappings();
    };

    async constructTableMappings() {
        this.caseDataTableMappings = await this._inboxService.transformCaseData(this.caseData, this.currentTask);
    }

    translateInboxLabel(label: string): string {
        return this.languageService.translateInboxLabel(label);
    }
}
